import React from "react";
import { useTranslation } from "react-i18next";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import { Badge } from "src/components/base/badge";
import {
    ArrowsRightLeftIcon,
    EllipsisVerticalIcon,
    LockClosedIcon,
    PencilSquareIcon,
    TagIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { Divider } from "src/components/base/divider";
import { Text } from "src/components/base/text";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";

/**
 * The properties for {@link TagRowAdmin}
 */
export type TagRowAdminProps = {
    /** The tags that should be rendered */
    tag: SimpleTag;
    /** Open the rename tag popup */
    setOpenRenameTag: (tag: SimpleTag) => void;
    /** Open the set tag description popup */
    setOpenSetTagDesc: (tag: SimpleTag) => void;
    /** Open the move tag from the directory popup */
    setOpenMoveFromDirectory: (tag: SimpleTag) => void;
    /** Open the delete tag popup */
    setOpenDeleteTag: (tag: SimpleTag) => void;
    /** Whether the tag is ungrouped */
    isUngrouped?: boolean;
};

/**
 * A single row in the tag overview table
 */
export function TagRowAdmin(props: TagRowAdminProps) {
    const [t] = useTranslation("admin-tag-overview");

    const navigate = useNavigate();

    const { tag, setOpenRenameTag, setOpenSetTagDesc, setOpenMoveFromDirectory, setOpenDeleteTag } = props;

    return (
        <>
            <Text
                onClick={async () => await navigate({ to: "/a/tags/$tagId/associated", params: { tagId: tag.uuid } })}
                className={clsx("flex items-center gap-3 hover:cursor-pointer", !props.isUngrouped && "ms-12")}
            >
                <TagIcon className={"size-4"} />
                <span className={"text-zinc-950 dark:text-white"}>{tag.name}</span>
                {tag.locked && (
                    <Badge color={"orange"}>
                        <LockClosedIcon className="size-4" />
                        <span>{t("label.read-only")}</span>
                    </Badge>
                )}
            </Text>
            <Text
                onClick={async () => await navigate({ to: "/a/tags/$tagId/associated", params: { tagId: tag.uuid } })}
                className={"w-[50ch] self-center text-wrap break-all whitespace-pre-wrap hover:cursor-pointer"}
            >
                {tag.description}
            </Text>
            <Dropdown>
                <DropdownButton plain={true}>
                    <EllipsisVerticalIcon />
                </DropdownButton>
                <DropdownMenu anchor={"bottom end"}>
                    <DropdownSection>
                        <DropdownItem disabled={tag.locked} onClick={() => setOpenRenameTag(tag)}>
                            <PencilSquareIcon />
                            <DropdownLabel>{t("button.rename-tag")}</DropdownLabel>
                        </DropdownItem>
                        <DropdownItem disabled={tag.locked} onClick={() => setOpenSetTagDesc(tag)}>
                            <PencilSquareIcon />
                            <DropdownLabel>{t("button.set-tag-description")}</DropdownLabel>
                        </DropdownItem>
                        <DropdownItem disabled={tag.locked} onClick={() => setOpenMoveFromDirectory(tag)}>
                            <ArrowsRightLeftIcon />
                            {t("button.move-tag-from-directory")}
                        </DropdownItem>
                    </DropdownSection>
                    <DropdownSection>
                        <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                        <DropdownItem disabled={tag.locked} onClick={() => setOpenDeleteTag(tag)}>
                            <TrashIcon />
                            <DropdownLabel>{t("button.delete-tag")}</DropdownLabel>
                        </DropdownItem>
                    </DropdownSection>
                </DropdownMenu>
            </Dropdown>
            <Divider className={"col-span-3"} />
        </>
    );
}
